import { useEffect } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';
import { Quote } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface Testimonial {
  name: string;
  title: string;
  company: string;
  quote: string;
  imagePlaceholder?: string;
}

// Testimonials are in German as they are real customer quotes
const testimonials: Testimonial[] = [
  {
    name: "Thomas Müller",
    title: "CEO",
    company: "rainmaker.travel",
    quote: "In leuchtende Hotelfotografie zu investieren, ist keine Ausgabe. Es ist eine strategische Investierung mit klarer Rendite. Die Hotelbilder von Thomas Haberland steigern nachweislich Markenwert, Gästevertrauen und Umsatz. Für unsere Kunden ist seine Arbeit ein zentraler Bestandteil einer ganzheitlichen Digital- und Vertriebsstrategie. Sie sorgt für stärkere Marktpositionierung, höhere ADR und RevPAR und schafft nachhaltiges Wachstum im Direktgeschäft. Sie tragen wesentlich zur wirtschaftlichen Nachhaltigkeit bei."
  },
  {
    name: "Florian Zumkeller",
    title: "Geschäftsführer",
    company: "Hotel Adler Häusern",
    quote: "Die neuen Fotos von Leuchtende Hotelfotografie haben nicht nur uns, sondern auch unsere Kunden überzeugt. Durch die neuen Aufnahmen konnten wir auf diversen Buchungsportalen einen erheblichen Zuwachs an Reservierungen verzeichnen. Schon nach wenigen Wochen hat sich die Investition in hervorragende Fotos voll amortisiert. Seither unterstützt uns die Agentur Haberland mit Fotografie und anderen Werbeaktivitäten. Ob Fotos, Mailings, Logo oder Hotelbroschüre – wir sind mit den Ergebnissen sehr zufrieden!"
  },
  {
    name: "Olaf Dreesen",
    title: "General Manager",
    company: "Rheinhotel Dreesen Bonn",
    quote: "Das weiße Haus am Rhein galt schon immer als strahlend elegantes Ensemble mit großer Historie – seit mehr als 120 Jahren! Mit seinen leuchtenden Hotelfotos hat Herr Haberland das Image unseres Hotels perfekt umgesetzt. Die erfrischende Lage am Wasser sowie die elegante Leichtigkeit der Einrichtung kommen dabei ideal zur Geltung. Auch die Großzügigkeit der mondänen Tagungsräume wird eindrucksvoll vermittelt."
  },
  {
    name: "Dr. Caroline von Kretschmann",
    title: "Inhaberin/Geschäftsführerin",
    company: "Hotel Europäischer Hof Heidelberg",
    quote: "Bereits zum 5. Mal fotografierte die Agentur Haberland für unser Hotel und liefert wie immer außergewöhnliche Qualität. Die leuchtende Hotelfotografie vermittelt mit ihrer Brillanz den Glanz und den Charme unseres traditionsreichen 5-Sterne-Hotels. Die Zusammenarbeit mit Herrn Haberland ist dabei hochprofessionell und sehr angenehm. Er arbeitet effizient, bringt kreative Vorschläge ein und versteht es, bei den Shootings den täglichen Hotelalltag nicht zu stören."
  },
  {
    name: "Birgit Lindner",
    title: "Administration Manager",
    company: "Atrium Hotel München",
    quote: "Die Agentur Haberland ist für uns ein zuverlässiger Partner für alle Bereiche unserer Marketingaktivitäten. Die Leistungen sind von ausgezeichneter Qualität und professionell auf den Bereich Hotellerie ausgerichtet. Neben leuchtenden Hotelaufnahmen erstellte die Agentur für uns eine neue Website, eine Imagebroschüre sowie Briefpapier und Visitenkarten nach CI-Vorgaben. Ein Buchungszuwachs von 20 % ist unser gemeinsamer Erfolg."
  },
  {
    name: "Stefan Irniger",
    title: "Marketing Manager",
    company: "SV Group, Division Hotel Zürich",
    quote: "Die Referenzbilder von Leuchtende Hotelfotografie haben uns mehr als überzeugt. Nach unserem ersten gemeinsamen Projekt – dem Renaissance Zürich Tower – haben wir weitere Fotoshootings (Courtyard Zürich, Residence und Courtyard München) gemeinsam umgesetzt. Gerade auch bei Aufnahmen mit Models zeigt sich die Professionalität sowohl im Ergebnis als auch in der Abwicklung. Die Agentur kümmert sich komplett um Modelcasting, Verträge, Styling und Organisation."
  }
];

export default function TestimonialCarousel() {
  const { t } = useTranslation();
  const [emblaRef, emblaApi] = useEmblaCarousel(
    { 
      loop: true,
      align: 'center',
      skipSnaps: false,
    },
    [Autoplay({ delay: 8000, stopOnInteraction: false })]
  );

  useEffect(() => {
    if (emblaApi) {
      emblaApi.on('settle', () => {
        if (emblaApi.canScrollPrev() === false || emblaApi.canScrollNext() === false) {
          emblaApi.reInit();
        }
      });
    }
  }, [emblaApi]);

  return (
    <section className="section bg-card relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-radial opacity-10" />
      <div className="container-page max-w-7xl relative z-10">
        <div className="text-center mb-12 mx-auto max-w-3xl">
          <h2 className="h2 mb-4 text-glow text-center">{t('testimonials.title')}</h2>
          <p className="subline-gold text-center">
            {t('testimonials.subtitle')}
          </p>
        </div>

        <div className="overflow-hidden" ref={emblaRef}>
          <div className="flex gap-6">
            {testimonials.map((testimonial, index) => (
              <div
                key={index}
                className="flex-[0_0_100%] min-w-0 md:flex-[0_0_80%] lg:flex-[0_0_60%]"
              >
                <div className="bg-background border border-border rounded-lg p-8 md:p-10 lg:p-12 h-full relative">
                  <Quote className="absolute top-4 left-4 w-8 h-8 text-primary opacity-30" />
                  
                  <div className="flex flex-col items-center text-center mb-6">
                    {/* Placeholder for image/logo */}
                    <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                      <span className="text-2xl font-semibold text-primary">
                        {testimonial.name.charAt(0)}
                      </span>
                    </div>
                    <h3 className="subline-gold--sm mb-1">{testimonial.name}</h3>
                    <p className="caption text-muted-foreground">
                      {testimonial.title} | {testimonial.company}
                    </p>
                  </div>

                  <blockquote className="body italic text-center leading-relaxed">
                    "{testimonial.quote}"
                  </blockquote>
                  
                  <Quote className="absolute bottom-4 right-4 w-8 h-8 text-primary opacity-30 rotate-180" />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Carousel indicators - minimum 44px touch targets */}
        <div className="flex justify-center gap-1 mt-8">
          {testimonials.map((_, index) => (
            <button
              key={index}
              onClick={() => emblaApi?.scrollTo(index)}
              className="w-11 h-11 flex items-center justify-center"
              aria-label={`Go to testimonial ${index + 1}`}
            >
              <span className="w-3 h-3 rounded-full bg-primary/30 hover:bg-primary/50 transition-colors" />
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}